import type { Doc } from "building-types/doc";
import type { PlanViewHandle } from "building-types/handles/docEntityHandleTypes";
import type { PlanView } from "building-types/views/View3D";
import { setIn } from "common/utils/objectUtils";
import { filterDocNativePaths } from "./utils/instanceScopedNamespacePathTools";

/** The level a plan view is placed by: its first visible member, else its recorded host level. */
export function planViewHostLevelId(view: PlanView): string | null {
  if (view.planViewType === "COMPOSITE") {
    return null;
  }
  return filterDocNativePaths(view.levelIds)[0]?.entityId ?? view.hostLevelId ?? null;
}

/** The levels placing a plan view: its visible members, else its host level. Empty when it has none. */
export function planViewPlacementLevelIds(view: PlanView): readonly string[] {
  const memberLevelIds = filterDocNativePaths(view.levelIds).map((member) => member.entityId);
  if (memberLevelIds.length > 0) {
    return memberLevelIds;
  }

  const hostLevelId = planViewHostLevelId(view);
  return hostLevelId === null ? [] : [hostLevelId];
}

/**
 * Keeps `hostLevelId` in step with a `levelIds` change. `previousView` recovers the host for views
 * authored before the field existed, whose only trace of it is the members the merge removed.
 */
export function docSyncPlanViewHostLevel(doc: Doc, handle: PlanViewHandle, previousView?: PlanView): Doc {
  const view = doc.planViews[handle.id];

  if (!view || view.planViewType === "COMPOSITE") {
    return doc;
  }

  const lostLevelIds = previousView && previousView.planViewType !== "COMPOSITE" ? previousView.levelIds : [];
  const hostLevelId = planViewHostLevelId(view) ?? filterDocNativePaths(lostLevelIds)[0]?.entityId;
  if (hostLevelId === undefined || hostLevelId === view.hostLevelId) {
    return doc;
  }

  return setIn(doc, ["planViews", handle.id, "hostLevelId"], hostLevelId);
}
